// Normalize any thrown value into a real Error with the full diagnostic
// payload preserved.
//
// Why this exists. React 19, the Next.js dev overlay, and the previous
// `reportError` implementation all fell back to `String(thrownValue)` when a
// non-Error value was thrown — which produces the useless literal string
// `"[object Object]"` and loses every structured field. The SDK in
// particular throws `PersistenceShapeViolationError` /
// `PersistenceSchemaMismatchError` instances carrying `category`, `path`,
// `kind` fields; without normalization those fields never reach Better
// Stack and never reach the dev console either.
//
// `normalizeThrown` returns a real `Error` so React / Next / Better Stack
// can format it normally, while `serializeForTelemetry` flattens the same
// payload into searchable string columns for the prod dashboard.

/** JSON.stringify replacer that survives bigint payloads (per the
 *  `feedback-morfi-json-bigint` memory) and circular references. */
// ALLOW-UNKNOWN: JSON.stringify replacer signature accepts any value type.
function makeSafeReplacer(): (key: string, value: unknown) => unknown {
  const seen = new WeakSet<object>();
  // ALLOW-UNKNOWN: JSON replacer callback receives heterogeneous values; we widen to `unknown` and narrow inside.
  return (_key: string, value: unknown): unknown => {
    if (typeof value === 'bigint') return value.toString();
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) return '[Circular]';
      seen.add(value);
    }
    return value;
  };
}

/** JSON.stringify that never throws — bigint-safe, circular-safe. */
// ALLOW-UNKNOWN: telemetry serializer accepts arbitrary thrown values (unknown is the correct type).
function safeStringify(value: unknown): string {
  try {
    const out: string = JSON.stringify(value, makeSafeReplacer()) ?? '';
    return out;
  } catch {
    // SILENT-CATCH: serialization must never throw — telemetry path.
    return '[Unserializable]';
  }
}

/** Look for a usable string `.message`-equivalent on a candidate object. */
function extractMessage(value: object): string | null {
  const obj: Record<string, unknown> = value as Record<string, unknown>;
  for (const key of ['message', 'error', 'reason', 'description']) {
    // ALLOW-UNKNOWN: dynamic property read on a thrown object — value shape is open.
    const v: unknown = obj[key];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return null;
}

/**
 * Normalize any thrown value into a real `Error`.
 *
 * - `Error` instances pass through unchanged.
 * - Plain objects produce a new `Error` whose `.message` is the object's
 *   own `message` field (or the JSON-serialised form if absent), with all
 *   own properties of the original copied onto the returned `Error` and
 *   the original attached as `.cause` so DevTools can drill in.
 * - Primitives / null / undefined produce a labelled `Error`.
 *
 * The returned value is always a real `Error`, so React / Next / Better
 * Stack format it normally instead of falling back to
 * `String(thrownValue)` (which produces "[object Object]").
 */
// ALLOW-UNKNOWN: error boundary — JavaScript's `throw` accepts any value (string/object/null), unknown is the correct type.
export function normalizeThrown(value: unknown): Error {
  if (value instanceof Error) return value;

  if (value === null || value === undefined) {
    return new Error(`Thrown value was ${value === null ? 'null' : 'undefined'}`);
  }

  if (typeof value !== 'object') {
    return new Error(`Thrown ${typeof value}: ${String(value)}`);
  }

  const message: string = extractMessage(value) ?? safeStringify(value);
  const err: Error = new Error(message);

  // Carry the original `name` if present so error classes survive.
  // ALLOW-UNKNOWN: speculative property read on a thrown object — open shape.
  const candidateName: unknown = (value as { name?: unknown }).name;
  if (typeof candidateName === 'string' && candidateName.length > 0) {
    err.name = candidateName;
  } else {
    err.name = 'NormalizedError';
  }

  // Keep the original retrievable via `.cause` so DevTools / breakpoints
  // can still see the live object identity.
  // ALLOW-UNKNOWN: writing `.cause` to attach the original thrown value (open shape).
  (err as { cause?: unknown }).cause = value;

  // Copy own properties from the original onto the Error so structured
  // fields (`category`, `path`, `kind`, etc.) survive without unwrapping
  // `.cause`. Skip the properties Error itself owns.
  for (const key of Object.getOwnPropertyNames(value)) {
    if (key === 'stack' || key === 'message' || key === 'name') continue;
    try {
      // ALLOW-UNKNOWN: copying arbitrary own-property keys from a thrown object onto the normalised Error.
      (err as unknown as Record<string, unknown>)[key] = (value as Record<string, unknown>)[key];
    } catch {
      // SILENT-CATCH: a getter on the source object may throw — best-effort copy.
    }
  }
  return err;
}

/** Truncate long strings to keep telemetry payloads bounded. */
function clip(value: string, limit: number): string {
  return value.length > limit ? `${value.slice(0, limit)}…` : value;
}

/** The set of fields stamped on every normalized Error — skipped when
 *  flattening so we don't duplicate them under both their bare name and
 *  the `error_*` prefix. */
const STANDARD_ERROR_KEYS: ReadonlySet<string> = new Set(['message', 'name', 'stack', 'cause']);

/**
 * Flatten an error (or any thrown value) into a `Record<string, string>`
 * suitable for the Better Stack `captureException` context. Every own
 * property of the value becomes an `error_<key>` column so the dashboard
 * can filter by `error_category`, `error_path`, etc. — the structured
 * diagnostic fields a `PersistenceShapeViolationError` carries.
 *
 * Always populates:
 *   - `error_class`      — the constructor / `.name`
 *   - `error_message`    — `.message`, or `String(value)` for primitives
 *   - `error_serialized` — JSON blob of the raw value, capped at 2 KB
 *
 * Stack traces, when present, are emitted as `error_stack` capped at 4 KB.
 */
// ALLOW-UNKNOWN: telemetry serializer accepts arbitrary thrown values (unknown is the correct type at the error boundary).
export function serializeForTelemetry(value: unknown): Record<string, string> {
  const out: Record<string, string> = {};

  if (value === null || value === undefined) {
    out.error_class = 'nullish';
    out.error_message = String(value);
    out.error_serialized = String(value);
    return out;
  }

  if (typeof value !== 'object') {
    out.error_class = typeof value;
    out.error_message = String(value);
    out.error_serialized = String(value);
    return out;
  }

  // Class / `.name` resolution: real Errors expose a meaningful `.name`;
  // plain objects sometimes set `name` explicitly; otherwise fall back to
  // the constructor name.
  // ALLOW-UNKNOWN: speculative property read on a thrown object — open shape.
  const nameField: unknown = (value as { name?: unknown }).name;
  const ctorName: string | undefined = (value as { constructor?: { name?: string } }).constructor
    ?.name;
  out.error_class =
    typeof nameField === 'string' && nameField.length > 0 ? nameField : (ctorName ?? 'PlainObject');

  const msg: string | null = value instanceof Error ? value.message : extractMessage(value);
  out.error_message = msg !== null && msg.length > 0 ? msg : '(empty)';

  if (value instanceof Error && typeof value.stack === 'string') {
    out.error_stack = clip(value.stack, 4000);
  }

  // ALLOW-UNKNOWN: `.cause` on a thrown object is part of the open Error-chain shape.
  const cause: unknown = (value as { cause?: unknown }).cause;
  if (cause !== undefined) {
    out.error_cause = clip(safeStringify(cause), 2000);
  }

  out.error_serialized = clip(safeStringify(value), 2000);

  // Flatten own properties so each structured field is its own column.
  for (const key of Object.getOwnPropertyNames(value)) {
    if (STANDARD_ERROR_KEYS.has(key)) continue;
    // ALLOW-UNKNOWN: enumerating own properties of a thrown object — open shape.
    const v: unknown = (value as Record<string, unknown>)[key];
    if (v === undefined) continue;
    const flat: string = typeof v === 'string' ? v : safeStringify(v);
    out[`error_${key}`] = clip(flat, 500);
  }

  return out;
}
